import { AudioItem } from '@/features/remotion-editor/editor/items/audio/audio-item-type';
import { CaptionsItem } from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import { EditorStarterItem } from '@/features/remotion-editor/editor/items/item-type';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import { getZoneForItemType } from '@/features/remotion-editor/editor/utils/zone-utils';
import { TrackType } from '@/features/remotion-editor/editor/state/types';

const TRACK_TYPE_ORDER = ['title', 'caption', 'audio', 'video'] as const;

/** main track을 새로 만들 때 기존 타임라인 타입 순서를 유지하기 위한 삽입 위치 계산기 */
const findTrackInsertIndex = (tracks: TrackType[], type: string): number => {
  const targetOrder = TRACK_TYPE_ORDER.indexOf(
    type as (typeof TRACK_TYPE_ORDER)[number],
  );
  if (targetOrder === -1) return tracks.length;

  for (let i = 0; i < tracks.length; i++) {
    const trackType = tracks[i]?.metadata?.type;
    const trackOrder = TRACK_TYPE_ORDER.indexOf(
      trackType as (typeof TRACK_TYPE_ORDER)[number],
    );
    if (trackOrder > targetOrder) {
      return i;
    }
  }

  return tracks.length;
};

/**
 * 재생성 대상 scene item을 main audio/caption track 기준으로만 모은다.
 * 대본 패널이 main track으로 scene을 구성하므로, 여기서도 같은 경계를 써야
 * non-main track의 shared item까지 재생성 범위에 끌려 들어오지 않는다.
 */
export const collectCurrentSceneItemsFromMainTracks = (
  tracks: TrackType[],
  items: Record<string, EditorStarterItem>,
  sceneId: number,
) => {
  const currentSceneAudioItems: AudioItem[] = [];
  const currentSceneCaptionItems: CaptionsItem[] = [];

  tracks.forEach((track) => {
    // 대본 패널은 main track 기준으로 scene을 구성하므로 재생성 대상도 같은 경계를 따른다.
    if (track.metadata?.isMainTrack !== true) {
      return;
    }

    track.items?.forEach((itemId) => {
      const item = items[itemId];

      if (item?.type === 'audio' && item.renderMeta?.sceneId === sceneId) {
        currentSceneAudioItems.push(item);
      }

      if (item?.type === 'captions' && item.renderMeta?.sceneId === sceneId) {
        currentSceneCaptionItems.push(item);
      }
    });
  });

  return {
    currentSceneAudioItems,
    currentSceneCaptionItems,
  };
};

/**
 * regenerated item을 main same-type track에만 다시 붙인다.
 * 해당 타입의 main track이 없으면 새 main track을 만들어 결과 유실을 막는다.
 */
export const rebuildTracksWithNewItemsOnMainTracks = ({
  tracks,
  items,
  addedItemIdsByType,
}: {
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  addedItemIdsByType: {
    audio: (string | undefined)[];
    caption: (string | undefined)[];
    video: (string | undefined)[];
  };
}) => {
  const normalizedAdded = {
    audio: addedItemIdsByType.audio.filter(
      (id): id is string => typeof id === 'string',
    ),
    caption: addedItemIdsByType.caption.filter(
      (id): id is string => typeof id === 'string',
    ),
    video: addedItemIdsByType.video.filter(
      (id): id is string => typeof id === 'string',
    ),
  };

  const existingMainTrackTypes = new Set(
    tracks
      .filter((track) => track.metadata?.isMainTrack === true)
      .map((track) => track.metadata?.type)
      .filter(Boolean),
  );

  const updatedTracks = tracks.map((track) => {
    const trackType = track.metadata?.type;
    // regenerated item이 non-main 같은 타입 트랙에 복제되는 버그를 막기 위해 main track에만 append한다.
    const appendIds =
      track.metadata?.isMainTrack === true &&
      (trackType === 'audio' ||
        trackType === 'caption' ||
        trackType === 'video')
        ? normalizedAdded[trackType]
        : [];

    const merged = [...track.items, ...appendIds].filter(
      (id): id is string => Boolean(id) && Boolean(items[id]),
    );
    const deduped = Array.from(new Set(merged));

    const sorted = deduped.sort((a, b) => {
      const fromA = items[a]?.from ?? 0;
      const fromB = items[b]?.from ?? 0;

      if (fromA === fromB) {
        return a.localeCompare(b);
      }

      return fromA - fromB;
    });

    return {
      ...track,
      items: sorted,
    };
  });

  const typesToCheck: Array<'audio' | 'caption' | 'video'> = [
    'audio',
    'caption',
    'video',
  ];

  for (const type of typesToCheck) {
    if (normalizedAdded[type].length > 0 && !existingMainTrackTypes.has(type)) {
      const newTrackItems = normalizedAdded[type]
        .filter((id) => Boolean(items[id]))
        .sort((a, b) => (items[a]?.from ?? 0) - (items[b]?.from ?? 0));

      if (newTrackItems.length > 0) {
        // non-main track만 남아 있는 편집 상태에서도 regenerated 결과가 유실되지 않도록 새 main track을 만든다.
        const newTrack = {
          id: generateRandomId(),
          items: newTrackItems,
          hidden: false,
          muted: false,
          locked: false,
          magnetic: false,
          zone: getZoneForItemType(type),
          metadata: {
            isMainTrack: true,
            trackType: 'main' as const,
            type,
          },
        };

        const insertIndex = findTrackInsertIndex(updatedTracks, type);
        updatedTracks.splice(insertIndex, 0, newTrack);
      }
    }
  }

  return updatedTracks;
};
